import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, MapPin, Ruler, ShieldCheck, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import JsonLd from "@/components/JsonLd";
import Lightbox from "@/components/Lightbox";
import { api, imgUrl, SITE_URL } from "@/lib/api";

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const FAQ = [
  {
    q: "Čo je podhľad a prečo ho riešiť pri zateplení fasády?",
    a: "Podhľad je spodná strana presahu strechy — tá časť, ktorú vidíte, keď stojite pri fasáde a pozriete hore. Pri zateplení fasády sa oplatí urobiť naraz: lešenie už stojí, takže práca navyše je minimálna a výsledok pôsobí kompletne.",
  },
  {
    q: "Koľko stojí zateplenie a obklad podhľadu?",
    a: "Orientačne 28–38 €/m² podľa zvoleného spôsobu montáže (laty, OSB debnenie alebo koníky). Presnú cenu vypočíta naša kalkulačka v presnom režime — stačí zadať obvod domu a šírku podhľadu, čielko 20 cm pripočítame automaticky.",
  },
  {
    q: "Aký je rozdiel medzi latami, OSB debnením a koníkmi?",
    a: "Laty každých 25 cm sú najlacnejšie a stačia pri rovnom krove. Plné debnenie z OSB dosiek je najpevnejšie a najčistejšie, ale najdrahšie. Koníky (nastaviteľné podpery) vyrovnajú nerovný podhľad bez rovnania väzníkov — rýchle a čisté riešenie.",
  },
  {
    q: "Musí sa podhľad vetrať?",
    a: "Áno. Pri montáži dbáme na vetraciu medzeru medzi izoláciou a obkladom, aby sa v krove nehromadila vlhkosť. Správne vetranie chráni väzníky aj izoláciu pred plesňou.",
  },
  {
    q: "Dá sa podhľad urobiť aj bez zateplenia fasády?",
    a: "Samozrejme. Podhľad vieme zrealizovať samostatne — napríklad keď je fasáda už hotová a chýba len čisté vyhotovenie presahu strechy.",
  },
];

const VARIANTS = [
  {
    key: "battens", name: "Laty každých 25 cm", priceKey: "soffit_battens_per_m2",
    points: ["Najlacnejší variant", "Drevená latová konštrukcia", "Stačí rovný krov", "Rýchla montáž"],
  },
  {
    key: "osb", name: "Plné debnenie z OSB", priceKey: "soffit_osb_per_m2",
    points: ["Najpevnejšie a najčistejšie", "Celoplošný OSB základ", "Rovná plocha bez vĺn", "Dlhá životnosť"],
  },
  {
    key: "koniky", name: "Koníky (nastaviteľné)", priceKey: "soffit_koniky_per_m2",
    points: ["Vyrovná nerovný podhľad", "Netreba rovnať väzníky", "Rýchle a čisté riešenie", "Ideálne pri starších krovoch"],
  },
];

export default function Podhlady() {
  const [rates, setRates] = useState(null);
  const [refs, setRefs] = useState([]);
  const [gallery, setGallery] = useState(null);

  useEffect(() => {
    document.title = "Zateplenie podhľadu — cena, varianty a realizácie | Zateplenia.eu";
    setMeta("description", "Zateplenie a obklad podhľadu strechy: 3 varianty montáže (laty, OSB debnenie, koníky), orientačné ceny €/m², ukážky realizácií a online kalkulačka presnej ceny.");
    window.scrollTo(0, 0);
    api.get("/pricing").then((r) => setRates(r.data?.precise_public || null)).catch(() => {});
    api.get("/references").then((r) => {
      const all = Array.isArray(r.data) ? r.data : [];
      setRefs(all.filter((x) => x.object_type === "podhlad"
        || `${x.title} ${x.description || ""}`.toLowerCase().includes("podhľad")));
    }).catch(() => {});
  }, []);

  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage", "@id": `${SITE_URL}/podhlady#faq`,
    "mainEntity": FAQ.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
  };

  return (
    <div className="bg-ink min-h-screen">
      <Header />
      <JsonLd id="ld-podhlady" data={faqLd} />

      <section className="max-w-[1200px] mx-auto px-5 md:px-10 pt-32 pb-16" data-testid="podhlady-page">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Detail, ktorý dotvorí fasádu</p>
          <h1 className="font-display text-white leading-[0.9] text-5xl sm:text-6xl lg:text-7xl">
            Zateplenie <span className="text-gold">podhľadu</span>
          </h1>
          <p className="text-white/60 text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
            Podhľad je spodná strana presahu strechy. Keď sa robí pri zateplení fasády, lešenie už stojí —
            takže práca navyše je minimálna a dom pôsobí hotovo od základov až po okap. Vyberte si spôsob montáže
            a cenu si vypočítajte do minúty.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/#kalkulacka" data-testid="podhlady-cta" className="bg-gold text-ink px-7 py-3.5 font-bold uppercase tracking-wider hover:bg-gold-light transition-colors flex items-center gap-2">
              Vypočítať cenu <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/partneri" className="border border-white/20 text-white px-7 py-3.5 font-bold uppercase tracking-wider hover:border-gold transition-colors">
              Naši partneri
            </Link>
          </div>
        </motion.div>
      </section>

      {/* 3 varianty montáže */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 pb-16">
        <h2 className="font-display text-3xl md:text-4xl text-white mb-2">Tri spôsoby montáže</h2>
        <p className="text-white/50 text-sm mb-8 max-w-2xl">Všetky ceny sú orientačné, za m² podhľadu vrátane čielka (20 cm hrana sa ráta automaticky). Presný výkaz dostanete v kalkulačke v presnom režime.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {VARIANTS.map((v, i) => {
            const price = rates?.[v.priceKey];
            return (
              <motion.div key={v.key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="border border-white/10 bg-ink-2 p-6 hover:border-gold/40 transition-colors" data-testid={`variant-${v.key}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="text-white font-semibold">{v.name}</h3>
                  {price != null && <span className="text-gold font-mono2 whitespace-nowrap" data-testid={`variant-price-${v.key}`}>{price} €/m²</span>}
                </div>
                <ul className="space-y-1.5">
                  {v.points.map((pt) => (
                    <li key={pt} className="text-white/55 text-sm flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-gold shrink-0 mt-0.5" />{pt}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
        <p className="text-white/40 text-xs mt-4 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-gold" /> Pri nerovnom krove odporúčame koníky — vyrovnajú výškové rozdiely bez zásahu do väzníkov.
        </p>
      </section>

      {/* Ako to prebieha */}
      <section className="border-y border-white/10 bg-ink-2/40">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-16">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-8">Ako to prebieha</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              ["1", "Obhliadka zdarma", "Zameriame obvod domu a šírku podhľadu, skontrolujeme stav krovu."],
              ["2", "Presný výkaz", "Podhľad = obvod × (šírka + čielko 20 cm). Cenu poznáte vopred, bez prekvapení."],
              ["3", "Montáž", "Izolácia, vetranie a obklad zvoleným variantom — pri zateplení fasády naraz."],
              ["4", "Odovzdanie", "Čisté ukončenie po celom obvode. Záruka 36 mesiacov na prácu."],
            ].map(([n, t, d]) => (
              <div key={n} className="border border-white/10 p-5">
                <div className="font-mono2 text-gold text-sm mb-2">{n}</div>
                <div className="text-white font-medium mb-1.5">{t}</div>
                <div className="text-white/50 text-sm leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Realizácie */}
      {refs.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-5 md:px-10 py-16">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-8">Realizácie podhľadov</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {refs.slice(0, 8).map((r, i) => (
              <button key={r.id || i} onClick={() => setGallery({ images: [{ url: r.image }], index: 0 })} data-testid={`podhlad-ref-${i}`}
                className="text-left border border-white/10 bg-ink-2 overflow-hidden group">
                {r.image && <img src={imgUrl(r.image)} alt={r.title} className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                <div className="p-3">
                  <div className="text-white text-sm font-medium truncate">{r.title}</div>
                  <div className="text-white/40 text-xs flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3 text-gold" />{r.location}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 pb-20">
        <h2 className="font-display text-3xl md:text-4xl text-white mb-8">Časté otázky</h2>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <details key={i} className="border border-white/10 group" data-testid={`podhlady-faq-${i}`}>
              <summary className="cursor-pointer list-none flex items-center justify-between p-5 text-white font-medium hover:text-gold transition-colors">
                {f.q}
                <span className="text-gold text-xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="px-5 pb-5 text-white/60 text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-gold/20 bg-gold/5">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-16 text-center">
          <Ruler className="w-8 h-8 text-gold mx-auto mb-4" />
          <h2 className="font-display text-3xl md:text-5xl text-white">Presnú cenu podhľadu máte do minúty</h2>
          <p className="text-white/55 mt-4 max-w-xl mx-auto text-sm md:text-base">
            V kalkulačke zapnite presný režim, zadajte obvod domu a šírku podhľadu — výkaz výmery
            spočíta lišty, parapety, sokel aj podhľad s porovnaním všetkých troch variantov.
          </p>
          <div className="flex items-center justify-center gap-2 text-white/45 text-xs mt-4 font-mono2">
            <ShieldCheck className="w-4 h-4 text-gold" /> Záruka 36 mes. práca · 60 mes. materiál
          </div>
          <Link to="/#kalkulacka" data-testid="podhlady-cta-bottom" className="inline-flex items-center gap-2 mt-8 bg-gold text-ink px-8 py-4 font-bold uppercase tracking-wider hover:bg-gold-light transition-colors">
            Otvoriť kalkulačku <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
      {gallery && <Lightbox images={gallery.images} index={gallery.index} onClose={() => setGallery(null)} />}
    </div>
  );
}
